import React from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Science,
  Groups,
  Security,
  TrendingUp,
  Assignment,
  LocationOn,
  People,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Hero Section Component
const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #ffffff 0%, #eff6ff 30%, #f0fdf4 70%, #ffffff 100%)",
        py: { xs: 8, sm: 12, md: 16 },
        px: { xs: 3, sm: 4, md: 6 },
        minHeight: { xs: "auto", md: "85vh" },
        display: "flex",
        alignItems: "center",
      }}
    >
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 6, md: 8 }}
          alignItems="center"
          sx={{ width: "100%" }}
        >
          {/* Left Side - Text Content */}
          <Box sx={{ flex: { xs: "none", md: 1 }, width: "100%" }}>
            <Stack
              spacing={{ xs: 2.5, sm: 3, md: 3.5 }}
              sx={{
                textAlign: { xs: "center", md: "left" },
                pr: { md: 4 },
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: {
                    xs: "1.75rem",
                    sm: "2.25rem",
                    md: "2.75rem",
                    lg: "3rem",
                  },
                  fontWeight: 700,
                  color: "#1E3a8a",
                  lineHeight: { xs: 1.2, md: 1.1 },
                  letterSpacing: "-0.02em",
                }}
              >
                Participate in Research.{" "}
                <Box
                  component="br"
                  sx={{ display: { xs: "none", sm: "block" } }}
                />
                <Typography
                  component="span"
                  sx={{
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    color: "#16a34a",
                  }}
                >
                  Help Advance Treatment
                </Typography>{" "}
                for Crohn's & Colitis.
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                  color: "text.secondary",
                  lineHeight: 1.6,
                  maxWidth: { xs: "100%", md: "520px" },
                  fontWeight: 400,
                }}
              >
                Join clinical trials that are shaping the future of inflammatory
                bowel disease treatment. Your participation makes a difference.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 2, sm: 2 }}
                sx={{
                  mt: { xs: 3, md: 4 },
                  justifyContent: { xs: "center", md: "flex-start" },
                  alignItems: { xs: "center", sm: "flex-start" },
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  // onClick={() => navigate("/registry/register")}
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.25, sm: 1.5 },
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    fontWeight: 600,
                    minWidth: "160px",
                    height: "48px",
                  }}
                >
                  Find a Study
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/registry/register")}
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.25, sm: 1.5 },
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    fontWeight: 600,
                    bgcolor: "#16a34a",
                    minWidth: "160px",
                    height: "48px",
                    "&:hover": {
                      bgcolor: "#15803d",
                    },
                  }}
                >
                  Join Patient Registry
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.25, sm: 1.5 },
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    fontWeight: 600,
                    minWidth: "160px",
                    height: "48px",
                  }}
                >
                  Am I Eligible?
                </Button>
              </Stack>
            </Stack>
          </Box>

          {/* Right Side - Image */}
          <Box sx={{ flex: { xs: "none", md: 1 }, width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
                mt: { xs: 6, md: 0 },
                pl: { md: 4 },
              }}
            >
              <Box
                component="img"
                src="/lab.png"
                alt="Medical research laboratory"
                sx={{
                  width: "100%",
                  maxWidth: { xs: "500px", md: "550px", lg: "650px" },
                  height: "auto",
                  borderRadius: "20px",
                  boxShadow: "0 12px 48px rgba(0,0,0,0.15)",
                  transform: { md: "perspective(1000px) rotateY(-5deg)" },
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    transform: { md: "perspective(1000px) rotateY(0deg)" },
                    boxShadow: "0 16px 64px rgba(0,0,0,0.2)",
                  },
                }}
              />
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

// Featured Studies Section
const FeaturedStudiesSection = () => {
  const studies = [
    {
      id: 1,
      title:
        "ADVANCE-UC: Novel Therapy for Moderate to Severe Ulcerative Colitis",
      description:
        "Evaluating the efficacy of an investigational oral medication for UC patients.",
      status: "Recruiting",
      location: "Multiple Locations",
      participants: "250 participants",
      requirements: [
        "Ages 18-75 with moderate to severe UC",
        "Failed or intolerant to at least one conventional therapy",
      ],
    },
    {
      id: 2,
      title: "PIONEER-CD: Biologic-Naïve Crohn's Disease Study",
      description:
        "Testing a novel biologic therapy for patients who have not yet tried biologics.",
      status: "Recruiting",
      location: "Remote + In-person",
      participants: "180 participants",
      requirements: [
        "Ages 18-65 with moderate to severe Crohn's",
        "No prior exposure to biologics",
      ],
    },
    {
      id: 3,
      title: "DIET-IBD: Dietary Interventions in IBD Management",
      description:
        "Investigating the impact of specific diets on IBD symptoms and inflammation.",
      status: "Recruiting",
      location: "Virtual Study",
      participants: "300 participants",
      requirements: [
        "Ages 18+ with confirmed IBD diagnosis",
        "Willing to follow a structured diet plan",
      ],
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, sm: 8, md: 10 }, px: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        <Stack spacing={6}>
          {/* Section Header */}
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                fontWeight: 700,
                color: "#1E3a8a",
                mb: 2,
              }}
            >
              Featured Study Opportunities
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", sm: "1.125rem" },
                color: "text.secondary",
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Explore current clinical trials seeking participants with Crohn's
              Disease and Ulcerative Colitis.
            </Typography>
          </Box>

          {/* Studies Cards */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            sx={{
              width: "100%",
              alignItems: "stretch",
            }}
          >
            {studies.map((study) => (
              <Box
                key={study.id}
                sx={{
                  flex: { xs: "none", md: 1 },
                  width: "100%",
                }}
              >
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, flexGrow: 1 }}>
                    <Stack spacing={2}>
                      {/* Study Status and Location */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LocationOn
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {study.location}
                          </Typography>
                        </Stack>
                        <Chip
                          label={study.status}
                          size="small"
                          sx={{
                            bgcolor: "#16a34a",
                            color: "white",
                            fontWeight: 600,
                          }}
                        />
                      </Stack>

                      {/* Study Title */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: { xs: "1rem", sm: "1.125rem" },
                          fontWeight: 600,
                          color: "#1E3a8a",
                          lineHeight: 1.3,
                        }}
                      >
                        {study.title}
                      </Typography>

                      {/* Study Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.5,
                        }}
                      >
                        {study.description}
                      </Typography>

                      {/* Participants Count */}
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <People
                          sx={{ fontSize: 16, color: "text.secondary" }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Seeking {study.participants}
                        </Typography>
                      </Stack>

                      {/* Requirements */}
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#1E3a8a",
                            mb: 1,
                          }}
                        >
                          Key Requirements:
                        </Typography>
                        <Stack spacing={0.5}>
                          {study.requirements.map((req, index) => (
                            <Stack
                              key={index}
                              direction="row"
                              alignItems="flex-start"
                              spacing={1}
                            >
                              <CheckCircle
                                sx={{
                                  fontSize: 14,
                                  color: "#16a34a",
                                  mt: 0.25,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.8rem",
                                  lineHeight: 1.4,
                                }}
                              >
                                {req}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>

                      {/* Learn More Button */}
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          mt: "auto",
                          alignSelf: "flex-start",
                        }}
                      >
                        Learn More
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      id: 1,
      title: "Pre-Screening",
      description:
        "Complete a brief questionnaire to determine if you might be eligible for a specific study.",
      icon: Assignment,
    },
    {
      id: 2,
      title: "Enrollment",
      description:
        "If eligible, you'll be contacted by the research team to discuss the study and complete the consent process.",
      icon: Groups,
    },
    {
      id: 3,
      title: "Participation",
      description:
        "Follow the study protocol, which may include visits, treatments, and completing questionnaires about your health.",
      icon: TrendingUp,
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        px: { xs: 2, sm: 3 },
        bgcolor: "primary.light",
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={6}>
          {/* Section Header */}
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                fontWeight: 700,
                color: "#1E3a8a",
                mb: 2,
              }}
            >
              How It Works
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", sm: "1.125rem" },
                color: "text.secondary",
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Participating in clinical research is a straightforward process.
              Here's what to expect.
            </Typography>
          </Box>

          {/* Steps Cards */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={4}
            sx={{
              width: "100%",
              alignItems: "stretch",
            }}
          >
            {steps.map((step) => (
              <Box
                key={step.id}
                sx={{
                  flex: { xs: "none", md: 1 },
                  width: "100%",
                }}
              >
                <Stack
                  spacing={3}
                  alignItems="center"
                  sx={{
                    textAlign: "center",
                    height: "100%",
                  }}
                >
                  {/* Step Icon */}
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: "#eff6ff",
                      border: "2px solid #1d4ed8",
                    }}
                  >
                    <step.icon
                      sx={{
                        fontSize: 36,
                        color: "#1d4ed8",
                      }}
                    />
                  </Avatar>

                  {/* Step Number and Title */}
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: "1.125rem", sm: "1.25rem" },
                        fontWeight: 700,
                        color: "#1E3a8a",
                        mb: 1,
                      }}
                    >
                      {step.id}. {step.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.6,
                        maxWidth: { xs: "100%", md: "280px" },
                        mx: "auto",
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>

          {/* Learn More Button */}
          <Box sx={{ textAlign: "center", pt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Learn More About the Process
            </Button>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

// Call to Action Section
const CallToActionSection = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        background: "#1E3a8a",
        py: { xs: 8, sm: 10, md: 12 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={4} alignItems="center" sx={{ textAlign: "center" }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              fontWeight: 700,
              color: "white",
              lineHeight: 1.2,
            }}
          >
            Ready to make a difference in IBD research?
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "1rem", sm: "1.125rem" },
              color: "rgba(255, 255, 255, 0.9)",
              maxWidth: "500px",
              lineHeight: 1.6,
            }}
          >
            Your participation in clinical trials helps researchers develop
            better treatments for Crohn's disease and ulcerative colitis.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ pt: 2 }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/study/clarimed-dev/register")}
              sx={{
                bgcolor: "white",
                color: "#1E3a8a",
                px: 4,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "#f8fafc",
                },
              }}
            >
              Find a Study
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: "white",
                color: "white",
                px: 4,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "white",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              Join Patient Registry
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Science,
      title: "Personalized Trial Matching",
      description:
        "Get matched with clinical trials that fit your unique health profile and preferences.",
    },
    {
      icon: Security,
      title: "Secure & Private",
      description:
        "Your health information is protected with enterprise-grade security and privacy measures.",
    },
    {
      icon: Groups,
      title: "Advance IBD Research",
      description:
        "Contribute to groundbreaking research that could lead to better treatments for future patients.",
    },
    {
      icon: TrendingUp,
      title: "Stay Informed",
      description:
        "Receive updates on the latest IBD research developments and treatment options.",
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, sm: 8, md: 10 }, px: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          {/* First Row - 2 Benefits */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={4}
            sx={{ width: "100%" }}
          >
            {benefits.slice(0, 2).map((benefit, index) => (
              <Box
                key={index}
                sx={{ flex: { xs: "none", sm: 1 }, width: "100%" }}
              >
                <Stack direction="row" spacing={3} alignItems="flex-start">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#eff6ff",
                    }}
                  >
                    <benefit.icon
                      sx={{
                        fontSize: 24,
                        color: "#1d4ed8",
                      }}
                    />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.125rem" },
                        fontWeight: 600,
                        color: "#1E3a8a",
                        mb: 1,
                      }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.6,
                      }}
                    >
                      {benefit.description}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>

          {/* Second Row - 2 Benefits */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={4}
            sx={{ width: "100%" }}
          >
            {benefits.slice(2, 4).map((benefit, index) => (
              <Box
                key={index + 2}
                sx={{ flex: { xs: "none", sm: 1 }, width: "100%" }}
              >
                <Stack direction="row" spacing={3} alignItems="flex-start">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#eff6ff",
                    }}
                  >
                    <benefit.icon
                      sx={{
                        fontSize: 24,
                        color: "#1d4ed8",
                      }}
                    />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.125rem" },
                        fontWeight: 600,
                        color: "#1E3a8a",
                        mb: 1,
                      }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.6,
                      }}
                    >
                      {benefit.description}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

const LandingPageV2 = () => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#ffffff",
      }}
    >
      <HeroSection />
      <FeaturedStudiesSection />
      <HowItWorksSection />
      <BenefitsSection />
      <CallToActionSection />
    </Box>
  );
};

export default LandingPageV2;
